import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Grid, Typography, Alert, CircularProgress } from '@mui/material';
import VideoPlayer from './VideoPlayer';
import NavigationControls from './NavigationControls';
import TelemetryPanel from './TelemetryPanel';
import CrossStartSetter from './CrossStartSetter';

/**
 * Complete video session container
 * Combines video player, navigation controls, and telemetry display
 * Handles all session-specific state and coordination
 */
function VideoSession({
  sessionData,
  isLoading = false,
  error = null,
  showTelemetry = true,
  showNavigation = true,
  layout = 'side-by-side', // 'side-by-side' | 'stacked' | 'video-only'
  className = '',
  onReload = null
}) {
  // Local state for video playback
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [skipSeconds, setSkipSeconds] = useState(5);
  const [hasAutoAdvanced, setHasAutoAdvanced] = useState(false);
  const [localCrossStart, setLocalCrossStart] = useState(sessionData?.crossStart || 0);

  // Calculate current lap and segment based on time
  const currentPosition = useMemo(() => {
    if (!sessionData?.lapData || sessionData.lapData.length === 0) {
      return { lap: 0, segment: 1 };
    }

    // Find current lap based on video time
    // Start with the first lap as default
    let currentLap = sessionData.lapData[0]?.number || 1;
    
    // Find the correct lap by checking start times
    for (let i = 0; i < sessionData.lapData.length; i++) {
      const lap = sessionData.lapData[i];
      const nextLap = sessionData.lapData[i + 1];
      
      if (currentTime >= lap.startTime) {
        // If there's no next lap, or current time is before next lap starts
        if (!nextLap || currentTime < nextLap.startTime) {
          currentLap = lap.number;
          break;
        }
      }
    }

    // TODO: Calculate current segment when segment data is available
    const currentSegment = 1;

    return { lap: currentLap, segment: currentSegment };
  }, [currentTime, sessionData?.lapData]);

  // Handle video time updates
  const handleTimeUpdate = useCallback((newTime) => {
    setCurrentTime(newTime);
  }, []);

  // Handle video play/pause
  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Handle navigation events
  const handleNavigateToLap = useCallback((lapNumber) => {
    const lap = sessionData?.lapData?.find(l => l.number === lapNumber);
    if (lap) {
      setCurrentTime(lap.startTime);
    }
  }, [sessionData?.lapData]);

  const handleNavigateToSegment = useCallback((segmentNumber) => {
    // TODO: Implement segment navigation when segment data is available
    console.log('Navigate to segment:', segmentNumber);
  }, []);

  const handleSkipSeconds = useCallback((seconds) => {
    const newTime = Math.max(0, currentTime + seconds);
    setCurrentTime(newTime);
  }, [currentTime]);

  const handleSkipLap = useCallback((direction) => {
    const currentIndex = sessionData?.lapData?.findIndex(lap => lap.number === currentPosition.lap);
    if (currentIndex !== undefined && currentIndex !== -1) {
      const newIndex = currentIndex + direction;
      if (newIndex >= 0 && newIndex < sessionData.lapData.length) {
        const newLap = sessionData.lapData[newIndex];
        setCurrentTime(newLap.startTime);
      }
    }
  }, [sessionData?.lapData, currentPosition.lap]);

  // Handle crossStart changes
  const handleCrossStartChange = useCallback((newCrossStart) => {
    setLocalCrossStart(newCrossStart);
    // Trigger session data reload to recalculate lap timings based on new crossStart
    if (onReload) {
      console.log('🔄 Reloading session data due to crossStart change');
      onReload();
    }
  }, [onReload]);

  // Auto-advance to 10 seconds before crossStart when session loads
  useEffect(() => {
    if (!hasAutoAdvanced && sessionData?.crossStart && sessionData.crossStart > 15) {
      const advanceTime = Math.max(0, sessionData.crossStart - 10);
      setCurrentTime(advanceTime);
      setHasAutoAdvanced(true);
    }
  }, [sessionData?.crossStart, hasAutoAdvanced]);

  // Update local crossStart when sessionData changes
  useEffect(() => {
    if (sessionData?.crossStart !== undefined) {
      setLocalCrossStart(sessionData.crossStart);
    }
  }, [sessionData?.crossStart]);

  // Loading state
  if (isLoading) {
    return (
      <Box 
        className={className}
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: 400,
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress />
        <Typography variant="h6" color="text.secondary">
          Loading session data...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box className={className} sx={{ p: 2 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Failed to load session
          </Typography>
          <Typography variant="body2">
            {error.message || 'An unknown error occurred'}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // No session data
  if (!sessionData) {
    return (
      <Box className={className} sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No session data available
        </Typography>
      </Box>
    );
  }

  // Determine what to show based on capabilities
  const capabilities = sessionData.capabilities;
  const shouldShowTelemetry = showTelemetry && capabilities.hasTelemetry;
  const shouldShowNavigation = showNavigation && capabilities.hasNavigation;

  // Layout configurations
  const getLayoutConfig = () => {
    if (layout === 'video-only') {
      return { videoWidth: 12, showSidebar: false };
    }
    
    if (layout === 'stacked') {
      return { videoWidth: 12, showSidebar: true, sidebarBelow: true };
    }
    
    // side-by-side (default)
    if (shouldShowTelemetry || shouldShowNavigation) {
      return { videoWidth: 8, showSidebar: true, sidebarBelow: false };
    }
    
    return { videoWidth: 12, showSidebar: false };
  };

  const layoutConfig = getLayoutConfig();

  return (
    <Box 
      className={className}
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}
    >
      {/* Header with session info */}
      <Box sx={{ 
        p: 1, 
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
        backgroundColor: 'background.paper'
      }}>
        <Typography variant="h6" noWrap>
          {sessionData.metadata.trackName} - Car #{sessionData.metadata.carNum}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {sessionData.metadata.drivername} • {sessionData.lapData.length} laps
          {capabilities.hasTelemetry && ' • Telemetry Available'}
        </Typography>
      </Box>

      {/* Main content area */}
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <Grid container sx={{ height: '100%' }}>
          {/* Video and Navigation Column */}
          <Grid 
            item 
            xs={12} 
            md={layoutConfig.videoWidth}
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              minHeight: 0
            }}
          >
            {/* Video Player */}
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
              <VideoPlayer
                videoUrl={sessionData.videoUrl}
                currentTime={currentTime}
                isPlaying={isPlaying}
                onTimeUpdate={handleTimeUpdate}
                onPlay={handlePlay}
                onPause={handlePause}
                onSeek={setCurrentTime}
                showControls={true}
              />
            </Box>

            {/* Navigation Controls (below video) */}
            {shouldShowNavigation && (
              <Box sx={{ 
                flexShrink: 0,
                borderTop: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper'
              }}>
                <NavigationControls
                  lapData={sessionData.lapData || []}
                  segments={sessionData.segments || []}
                  currentLap={currentPosition.lap}
                  currentSegment={currentPosition.segment}
                  skipSeconds={skipSeconds}
                  onNavigateToLap={handleNavigateToLap}
                  onNavigateToSegment={handleNavigateToSegment}
                  onSkipSeconds={handleSkipSeconds}
                  onSkipLap={handleSkipLap}
                  onSetSkipSeconds={setSkipSeconds}
                  showLapControls={capabilities.hasLapData}
                  showSegmentControls={capabilities.hasSegments}
                  compact={true}
                />
              </Box>
            )}
          </Grid>

          {/* Telemetry/Sidebar Column */}
          {layoutConfig.showSidebar && (
            <Grid 
              item 
              xs={12} 
              md={12 - layoutConfig.videoWidth}
              sx={{ 
                borderLeft: { md: layoutConfig.sidebarBelow ? 'none' : '1px solid' },
                borderTop: { xs: '1px solid', md: layoutConfig.sidebarBelow ? '1px solid' : 'none' },
                borderColor: 'divider',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Telemetry Panel */}
              {shouldShowTelemetry && (
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                  <TelemetryPanel
                    telemetryData={sessionData.telemetry}
                    currentTime={currentTime}
                    currentLap={currentPosition.lap}
                    lapData={sessionData.lapData}
                    showCharts={true}
                    showRealTimeData={true}
                    showLapStats={true}
                  />
                </Box>
              )}

              {/* Extended Navigation (sidebar mode) */}
              {shouldShowNavigation && !shouldShowTelemetry && (
                <Box sx={{ p: 2 }}>
                  <NavigationControls
                    lapData={sessionData.lapData || []}
                    segments={sessionData.segments || []}
                    currentLap={currentPosition.lap}
                    currentSegment={currentPosition.segment}
                    skipSeconds={skipSeconds}
                    onNavigateToLap={handleNavigateToLap}
                    onNavigateToSegment={handleNavigateToSegment}
                    onSkipSeconds={handleSkipSeconds}
                    onSkipLap={handleSkipLap}
                    onSetSkipSeconds={setSkipSeconds}
                    showLapControls={capabilities.hasLapData}
                    showSegmentControls={capabilities.hasSegments}
                    compact={false}
                  />
                </Box>
              )}
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Status Bar */}
      <Box sx={{ 
        p: 1, 
        borderTop: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1
      }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Lap {currentPosition.lap}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Lap Time: {Math.round(currentTime)}s
          </Typography>
          <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
            Video Time: {currentTime.toFixed(1)}s
          </Typography>
          <CrossStartSetter
            videoId={sessionData.id}
            crossStart={localCrossStart}
            currentVideoTime={currentTime}
            rawVideoData={sessionData.raw}
            onCrossStartChange={handleCrossStartChange}
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {capabilities.hasLapData && (
            <Typography variant="caption" color="primary">
              LAP DATA
            </Typography>
          )}
          {capabilities.hasTelemetry && (
            <Typography variant="caption" color="secondary">
              TELEMETRY
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default VideoSession; 